import { Box, Tab, Tabs } from "@mui/material";
import { RuleFactoryType, TabKeys } from "@repo/server-api/types";
import { useUserRoles } from "@repo/shared/components";
import React, { useEffect, useState } from "react";

import { errorStateHasMessages } from "@repo/legacy/components/helper/Index";

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
	rulefactory?: RuleFactoryType | undefined;
}

function CustomTabPanel(props: Readonly<TabPanelProps>) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}>
			{value === index ? (
				<Box sx={{ p: 3 }}>{children}</Box>
			) : (
				<Box sx={{ display: "none" }}>{children}</Box>
			)}
		</div>
	);
}

export default function CustomTabs(
	props: Readonly<{
		tabs: { label: string; key: TabKeys; content: React.ReactNode }[];
		tabIndex: number;
		setTabIndex: (value: number) => void;
		errors?: Record<string, string>;
		rulefactory?: RuleFactoryType | undefined;
	}>,
) {
	const [tabIndex, setTabIndex] = useState(0);
	const [tabs, setTabs] = useState(props.tabs);
	const roles = useUserRoles();

	useEffect(() => {
		// Filter tabs based on user roles and rulefactory permissions
		if (props.rulefactory) {
			setTabs(
				props.tabs.filter((tab) => props.rulefactory!.allowedTabsForUser(tab.key, roles.roles)),
			);
		} else {
			setTabs(props.tabs);
		}
	}, [roles, props.tabs]);

	useEffect(() => {
		setTabIndex(props.tabIndex);
	}, [props.tabIndex]);

	const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
		setTabIndex(newValue);
		props.setTabIndex(newValue);
	};

	return (
		<Box className="w-100 clear">
			<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
				<Tabs
					value={tabIndex}
					onChange={(e, newValue) => {
						handleChangeTab(e, newValue);
					}}
					aria-label="basic tabs"
					variant="scrollable"
					scrollButtons={false}
					sx={{
						"& .MuiTabs-flexContainer": {
							flexWrap: "wrap",
						},
						"& .MuiTabs-indicator": {
							display: "none",
						},
						"& .Mui-selected": {
							borderBottom: "2px solid",
							borderColor: "primary.main",
						},
					}}>
					{tabs.map((tab, index) => (
						<Tab
							wrapped
							key={tab.key}
							label={tab.label}
							id={`simple-tab-${index}`}
							aria-controls={`simple-tabpanel-${index}`}
							data-testid={`tab-${tab.key}`}
							className={
								props.errors && errorStateHasMessages({ [tab.key]: props.errors[tab.key] })
									? "red"
									: ""
							}
						/>
					))}
				</Tabs>
			</Box>
			{tabs.map((tab, index) => (
				<CustomTabPanel
					key={"tab-content-" + index}
					value={tabIndex}
					index={index}
					rulefactory={props.rulefactory}>
					{tab.content}
				</CustomTabPanel>
			))}
		</Box>
	);
}
